/**
 * Stage the patient cast in store state (no time travel): each cast member is
 * already at their stage on demo day. Real visits for the staged episodes exist
 * in the tenant (placed by the SQL seed); `linkPlanVisits` attaches them once the
 * horizon read binds the tenant.
 */
import { applyAuthLine, authDueItems, evalAuthLine, selectAuthCoverage } from "../engine/authLedger";
import { addDaysToKey, addWorkingDaysToKey, dateKeyAt, isoWeekday, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { initialGates } from "../engine/gates";
import { createHold } from "../engine/holds";
import { clampToSocWindow, socDueWindow } from "../engine/socWindow";

export { clampToSocWindow };
import { planConflictItems } from "../engine/constraints";
import { expandEpisode } from "@caredna-marlin/utils/homeHealthDemo/engine/planExpansion";
import { bookableWeekdays, doorLearnedWeekday, HORIZON_AFTER, HORIZON_BEFORE } from "@caredna-marlin/utils/homeHealthDemo/seed/schedule";
import { scheduledDays, scheduleFor } from "./scheduledDays";
import { applyRecertRule, applySupervisoryRule } from "@caredna-marlin/utils/homeHealthDemo/engine/rules";
import { CAST, isUnadmitted, PROVISIONAL_VISIT_OFFSET_DAYS, PROVISIONAL_VISIT_START, socDateFor, type CastMember } from "@caredna-marlin/utils/homeHealthDemo/fixtures/cast";
import { admittedMondayConstraints, REFERRAL_FIXTURES } from "@caredna-marlin/utils/homeHealthDemo/fixtures/patients";
import type { ConfigState, DateKey, DemoPatient, Discipline, Episode, Hold, HoldReadiness, IsoString, PlanVisit, Referral, RosterEntry, WorklistItem } from "@caredna-marlin/utils/homeHealthDemo/state";

export interface CastSeed {
  referrals: Record<string, Referral>;
  holds: Record<string, Hold>;
  readiness: Record<string, HoldReadiness>;
  episodes: Record<string, Episode>;
  planVisits: Record<string, PlanVisit>;
  items: WorklistItem[];
  /** Patient constraints already captured before demo day (welcome calls that happened). */
  constraints: Record<string, DemoPatient["constraints"]>;
  /** The cast decides each member's payer; the census fixture's rotation yields to it. */
  payers: Record<string, DemoPatient["payer"]>;
}

/**
 * **A plan visit is real exactly where the seed put a visit for it.** Placed by
 * the schedule: delivered before today, real from today on. Not placed: history
 * before today reads delivered, and anything later is a placeholder the store
 * places itself. The tenant and the store therefore agree on which plan visits
 * have a real visit behind them, with no second window to keep in step.
 */
export function placedStatuses(visits: PlanVisit[], today: DateKey, placed: ReadonlyMap<string, DateKey>): PlanVisit[] {
  return visits.map((v) => {
    if (v.rule === "recert") return v; // assessments the branch has not placed yet stay placeholders
    if (placed.has(v.id)) return { ...v, status: v.targetDate < today ? "completed" : "real" };
    if (v.targetDate < today) return { ...v, status: "completed" };
    return v;
  });
}

function episodeFor(member: CastMember, config: ConfigState, socDate: DateKey, demoToday: DateKey): { episode: Episode; visits: PlanVisit[] } {
  const { episode, visits: expanded } = expandEpisode({ ref: member.ref, socDate, caseType: member.caseType, orders: member.orders, requiredTraits: member.requiredTraits, pendingDisciplines: member.pendingDisciplines, availableWeekdays: bookableWeekdays(member.ref, demoToday) }, config);
  let visits = expanded;
  visits = applySupervisoryRule(visits, episode, config);
  visits = applyRecertRule(visits, episode, config, "RN");
  // Primary clinicians from the cast's team.
  visits = visits.map((v) => ({ ...v, providerHandle: v.visitTypeKind === "SOC" || v.rule === "recert" ? member.team.assessor ?? member.team.RN : member.team[v.discipline as keyof CastMember["team"]] ?? member.team.RN }));
  return { episode, visits };
}

/**
 * The script places some patients on a repeating weekday — Halvorsen every week on
 * the weekday of the next working day at 10:00, Osei on that day and the one after.
 * The pins are working-day offsets from demo day, so the weekday they resolve to
 * depends on the day the seed was built for. The SQL seed places their real visits
 * there, and `linkPlanVisits` binds a plan visit to a real one by date, so the plan
 * visits have to land on the same days: each week's visits map onto the pinned
 * weekdays in order.
 */
export function retimeToPinnedDays(visits: PlanVisit[], member: CastMember, demoToday: DateKey, socDate?: DateKey): PlanVisit[] {
  const pins = (member.pinnedVisits ?? []).filter((p) => p.everyWeek);
  if (!pins.length) return visits;
  const out = [...visits];
  for (const kind of new Set(pins.map((p) => p.kind))) {
    const weekdays = pins
      .filter((p) => p.kind === kind)
      .map((p) => isoWeekday(addWorkingDaysToKey(demoToday, p.dayOffset)))
      .sort((a, b) => a - b);
    // By plan week (the start of care's weekday), so a re-timed visit stays in the week it belongs to;
    // seven days hold every weekday once, so each pinned weekday has its date inside the week.
    const weekStartOf = (v: PlanVisit) => (socDate ? addDaysToKey(socDate, v.weekIndex * 7) : weekStartKey(v.targetDate));
    const byWeek = new Map<DateKey, PlanVisit[]>();
    for (const v of out) {
      if (v.visitTypeKind !== kind) continue;
      const week = weekStartOf(v);
      byWeek.set(week, [...(byWeek.get(week) ?? []), v]);
    }
    for (const [week, weekVisits] of byWeek) {
      const dates = Array.from({ length: 7 }, (_, i) => addDaysToKey(week, i)).filter((d) => weekdays.includes(isoWeekday(d)));
      weekVisits
        .sort((a, b) => a.targetDate.localeCompare(b.targetDate))
        .forEach((v, i) => {
          if (i >= dates.length) return;
          out[out.indexOf(v)] = { ...v, targetDate: dates[i] };
        });
    }
  }
  return out;
}

function maLedger(episode: Episode, disciplines: Discipline[], units: Partial<Record<Discipline, number>>, toDate: DateKey): Episode {
  let ledger = [evalAuthLine("RN", episode.socDate)];
  for (const d of disciplines) {
    ledger = applyAuthLine(ledger, { discipline: d, unitsAuthorized: units[d] ?? 6, unitsUsed: 0, fromDate: addDaysToKey(episode.socDate, 1), toDate });
  }
  return { ...episode, authLedger: ledger };
}

/**
 * Constraints captured before demo day: at a welcome call, or at the door by the
 * clinician who learned something. The patient admitted two days ago carries the
 * note Dana added after yesterday's visit, dated to the day she made it.
 */
function seededConstraints(demoToday: DateKey, tz: string): Record<string, DemoPatient["constraints"]> {
  return {
    "census-01": admittedMondayConstraints(dateKeyAt(addDaysToKey(demoToday, -1), "16:30", tz).toISOString(), doorLearnedWeekday(scheduleFor(demoToday).visits)),
  };
}

/**
 * The referral behind a pre-admission hold, as intake sent it: the disciplines
 * ordered and the traits the case needs, **not** a visit count. A referral that
 * really did arrive with frequencies (a hospital discharge with orders on file)
 * keeps them; everything else is eval-and-treat and the expected plan is the
 * branch's judgment, suggested from the case type.
 */
function referralFor(member: CastMember, config: ConfigState, demoToday: DateKey, receivedAtIso: IsoString, status: Referral["status"]): Referral {
  const fixture = REFERRAL_FIXTURES.find((r) => r.patientRef === member.ref);
  const stated = member.orders ?? fixture?.orders;
  const orders =
    stated ??
    (config.starterFrequencies[member.caseType] ?? []).map((d) => ({
      discipline: d.discipline,
      pattern: "eval",
      ...(d.requiredTraits?.length || member.requiredTraits?.length ? { requiredTraits: Array.from(new Set([...(d.requiredTraits ?? []), ...(d.discipline === "RN" ? member.requiredTraits ?? [] : [])])) } : {}),
    }));
  return {
    id: fixture?.id ?? `ref-${member.ref}`,
    patientRef: member.ref,
    source: fixture?.source ?? "Swedish First Hill · hospital discharge",
    receivedAtIso,
    orders,
    caseType: member.caseType,
    status,
    expectedDischarge: addWorkingDaysToKey(demoToday, member.dischargeOffset ?? 1),
  };
}

export function seedCast(config: ConfigState, patients: Record<string, DemoPatient>, demoToday: DateKey, nowIso: IsoString): CastSeed {
  const tz = config.branch.timeZone;
  const out: CastSeed = { referrals: {}, holds: {}, readiness: {}, episodes: {}, planVisits: {}, items: [], constraints: seededConstraints(demoToday, tz), payers: {} };
  const put = (visits: PlanVisit[]) => visits.forEach((v) => (out.planVisits[v.id] = v));

  for (const member of CAST) {
    const patient = patients[member.ref];
    if (!patient) continue;
    out.payers[member.ref] = member.payer;

    if (isUnadmitted(member.stage)) {
      // 1.2: intake has the referral but it has not reached the queue yet; the event flips it to new.
      if (member.stage === "referralPending") {
        const fixture = REFERRAL_FIXTURES.find((r) => r.patientRef === member.ref);
        const receivedAtIso = dateKeyAt(demoToday, fixture?.receivedAt ?? "09:40", tz).toISOString();
        const referral = referralFor(member, config, demoToday, receivedAtIso, "pending");
        out.referrals[referral.id] = referral;
        continue;
      }
      // 1.3: accepted referrals whose start of care is held but not yet converted.
      //
      // The cast's offsets are **working** days (F1, so every act reads on any
      // weekday), but the 48-hour SOC clock is **calendar** hours and the branch
      // has a weekend rotation. A hold placed a working day out can therefore
      // land after the day it is due by — on a Friday seed, discharge Friday and
      // a hold on Monday. The window wins: a hold never sits outside the two days
      // the start of care is due in.
      const expectedDischarge = addWorkingDaysToKey(demoToday, member.dischargeOffset ?? 1);
      const holdDate = clampToSocWindow(addWorkingDaysToKey(demoToday, member.hold?.dayOffset ?? 1), expectedDischarge);
      const receivedAtIso = dateKeyAt(addDaysToKey(holdDate, -1), "10:15", tz).toISOString();
      const referral = referralFor(member, config, demoToday, receivedAtIso, "accepted");
      out.referrals[referral.id] = referral;
      const hold = createHold(referral, {
        providerHandle: member.hold?.handle ?? member.team.assessor ?? member.team.RN,
        date: holdDate,
        windowStart: member.hold?.windowStart ?? "08:00",
        windowEnd: member.hold?.windowEnd ?? "11:00",
      }, config);
      out.holds[hold.id] = hold;
      const gates = initialGates(config, member.payer, { ordersOnFile: true, consentOnFile: member.stage !== "expiredHold" });
      out.readiness[hold.id] = member.stage === "expiredHold" ? { ...gates, consent: "needsAction" } : gates;
      continue;
    }

    const socDate = socDateFor(member, demoToday);
    if (!socDate) continue;
    let { episode, visits } = episodeFor(member, config, socDate, demoToday);
    // 2.3: assessed yesterday, nothing routine plotted until the admission event fires.
    visits = retimeToPinnedDays(visits, member, demoToday, socDate);
    // Where the seed placed a plan visit, the plan visit sits there too, and is real (`scheduledDays`).
    const placed = scheduledDays(demoToday);
    visits = visits.map((v) => (placed.has(v.id) ? { ...v, targetDate: placed.get(v.id)! } : v));
    visits = placedStatuses(visits, demoToday, placed);

    if (member.payer === "MA") {
      const disciplines = Array.from(new Set(visits.map((v) => v.discipline)));
      // Where the cast names the authorized units, only those disciplines run out; the rest are authorized through the episode so the patient carries one auth row, not four.
      const units: Partial<Record<Discipline, number>> = member.realVisitCap ? { RN: 30, PT: 30, OT: 30, SLP: 30, MSW: 30, HHA: 30, ...member.realVisitCap } : { RN: 8, PT: 6 };
      // A capped cast member's units are what runs out, not the authorization period, so the patient carries one auth row.
      episode = maLedger(episode, disciplines, units, member.realVisitCap ? episode.certEndDate : addDaysToKey(episode.socDate, 29));
      let coverage = selectAuthCoverage(episode, visits, true);
      // Visits beyond the authorized units are placeholders until the auth event lands.
      visits = visits.map((v) => (coverage.byVisitId[v.id] === "beyondAuth" && v.status === "real" ? { ...v, status: "placeholder" } : v));
      // 3.1 / 3.4: the first visit past the units is the provisional one, next week at 1:00, on the phone and on My week alike.
      if (member.stage === "pendingAuthNextWeek") {
        const first = visits.filter((v) => v.discipline === "RN" && coverage.byVisitId[v.id] === "beyondAuth").sort((a, b) => a.targetDate.localeCompare(b.targetDate))[0];
        if (first) {
          visits = visits.map((v) => (v.id === first.id ? { ...v, targetDate: addDaysToKey(demoToday, PROVISIONAL_VISIT_OFFSET_DAYS), windowStart: PROVISIONAL_VISIT_START, status: "placeholder" } : v));
          coverage = selectAuthCoverage(episode, visits, true);
        }
      }
      out.items.push(...authDueItems(episode.id, member.ref, patient.displayName, coverage, nowIso, out.items));
    }
    out.episodes[episode.id] = episode;
    put(visits);
  }
  // The conflict the worklist shows from 1.4 on: a clinical window that collides with what a
  // welcome call captured before demo day. Derived from the plan, so the patient page and the
  // worklist always agree.
  const seeded = { config, episodes: out.episodes, planVisits: out.planVisits, patients: withSeededConstraints(patients, out.constraints), roster: {} as Record<string, RosterEntry> };
  for (const ref of Object.keys(out.constraints)) out.items.push(...planConflictItems(seeded, ref, nowIso));
  return out;
}

/**
 * **Every admitted patient outside the cast carries their plan too**, so a card
 * for any of them reads a plan, a period and a week rather than a bare prior
 * visit. The plan is what the branch actually scheduled: each plan visit sits
 * where the seed placed it, and inside the seeded horizon a visit the seed found
 * no room for is not in the plan at all, so nothing reads as ordered but undone
 * or adds demand twice. Before the horizon it is history; after it, a
 * placeholder like anyone's later weeks. No rules and no worklist items — these
 * patients are background — and a Medicare Advantage patient is authorized
 * through the episode, so no auth row appears for them.
 */
export function seedCensusEpisodes(config: ConfigState, demoToday: DateKey, castRefs: ReadonlySet<string>): Pick<CastSeed, "episodes" | "planVisits"> {
  const out: Pick<CastSeed, "episodes" | "planVisits"> = { episodes: {}, planVisits: {} };
  const placed = scheduledDays(demoToday);
  const horizonStart = addDaysToKey(demoToday, -HORIZON_BEFORE);
  const horizonEnd = addDaysToKey(demoToday, HORIZON_AFTER);
  for (const p of scheduleFor(demoToday).patients) {
    if (castRefs.has(p.ref) || !p.socDate) continue;
    const expanded = expandEpisode({ ref: p.ref, socDate: p.socDate, caseType: p.caseType, orders: p.orders, requiredTraits: p.requiredTraits, availableWeekdays: bookableWeekdays(p.ref, demoToday) }, config);
    let episode: Episode = { ...expanded.episode, background: true };
    let visits: PlanVisit[] = expanded.visits
      .map((v) => (placed.has(v.id) ? { ...v, targetDate: placed.get(v.id)! } : v))
      .filter((v) => placed.has(v.id) || v.targetDate < horizonStart || v.targetDate > horizonEnd)
      .map((v) => ({ ...v, providerHandle: v.visitTypeKind === "SOC" ? p.team.assessor ?? p.team.RN : p.team[v.discipline as keyof CastMember["team"]] ?? p.team.RN }));
    visits = placedStatuses(visits, demoToday, placed);
    if (p.payer === "MA") {
      const disciplines = Array.from(new Set(visits.map((v) => v.discipline)));
      episode = maLedger(episode, disciplines, { RN: 60, PT: 60, OT: 60, SLP: 60, MSW: 60, HHA: 60 }, episode.certEndDate);
    }
    out.episodes[episode.id] = episode;
    for (const v of visits) out.planVisits[v.id] = v;
  }
  return out;
}

function withSeededConstraints(patients: Record<string, DemoPatient>, constraints: CastSeed["constraints"]): Record<string, DemoPatient> {
  const out = { ...patients };
  for (const [ref, list] of Object.entries(constraints)) if (out[ref]) out[ref] = { ...out[ref], constraints: list };
  return out;
}
